import React from 'react';
import { useParams } from 'react-router-dom';
import BaseForm, { ParamTypes } from '../../components/form/baseForm/BaseForm';
import TranslatableText from '../../components/i18n/TranslatableText';
import { RoutingValues } from '../../types/Shared.types';
import FormPartial from '../../components/form/formContainer/FormPartial';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import useSupplierTypeForm from './hooks/useSupplierTypeForm';
import { SupplierTypeFormDefs } from '../../constants/i18n/translations/termDefinitions/supply-chain';
import NumericInput from '../../components/form/formInputs/NumericInput/NumericInput';
import TextInput from '../../components/form/formInputs/TextInput/TextInput';
import { SupplierTypeRevisionHistoryFields } from '../../constants/revisionHistory/supplierApplicationRevisionHistory';

const SupplierTypeForm = () => {
    const { id } = useParams<ParamTypes>();
    const {
        fields,
        handleFieldChange,
        formMethods,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        isLoading,
        termSet,
        canCreate,
        canUpdate,
        canDelete,
        auditableFields,
    } = useSupplierTypeForm(id);

    return (
        <BaseForm
            title={
                <>
                    <TranslatableText
                        termKey={SupplierTypeFormDefs.Title}
                        termSet={termSet}
                    />{' '}
                    {fields?.code}
                </>
            }
            formMethods={formMethods}
            isLoading={isLoading}
            isNew={id === RoutingValues.newId}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onCancel={closeForm}
            showOnRevision={true}
            fieldMappings={SupplierTypeRevisionHistoryFields}
            entityUuid={fields?.uuid}
            displayCreateButton={canCreate}
            displayUpdateButton={canUpdate}
            displayDeleteButton={canDelete}
            auditableFields={auditableFields}>
            <FormPartial>
                <Grid container flexDirection="column" spacing={1}>
                    <Grid item xs={1}>
                        <NumericInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierTypeFormDefs.SortOrder}
                                />
                            }
                            id="sortOrder"
                            value={fields.sortOrder}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierTypeFormDefs.Code}
                                />
                            }
                            id="code"
                            value={fields.code}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={30}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label={
                                <TranslatableText
                                    termSet={termSet}
                                    termKey={SupplierTypeFormDefs.Description}
                                />
                            }
                            id="description"
                            value={fields.description}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={30}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termKey={SupplierTypeFormDefs.TypeActive}
                                    termSet={termSet}
                                />
                            }
                            control={
                                <Checkbox
                                    id="typeActive"
                                    checked={fields.typeActive}
                                    onChange={handleFieldChange}
                                    required={true}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termKey={SupplierTypeFormDefs.Default}
                                    termSet={termSet}
                                />
                            }
                            control={
                                <Checkbox
                                    id="default"
                                    checked={fields.default}
                                    onChange={handleFieldChange}
                                    required={true}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            label={
                                <TranslatableText
                                    termKey={
                                        SupplierTypeFormDefs.EmergencyResponse
                                    }
                                    termSet={termSet}
                                />
                            }
                            control={
                                <Checkbox
                                    id="emergencyResponse"
                                    checked={fields.emergencyResponse}
                                    onChange={handleFieldChange}
                                    required={true}
                                />
                            }
                        />
                    </Grid>
                </Grid>
            </FormPartial>
        </BaseForm>
    );
};

export default SupplierTypeForm;
